import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import Icon from '../AppIcon';
import MashhadLogo from '../MashhadLogo';
import { formatNumber } from '../../utils/formatNumber';
import { useAuth } from '../../contexts/AuthContext';

const Footer = () => {
  const navigate = useNavigate();
  const { userProfile, authStatus, AUTH_STATE } = useAuth();

  if (Capacitor.isNativePlatform()) return null;

  // على الويب: إخفاء الفوتر للمصور المسجّل فقط — يبقى للعميل والزائر
  if (authStatus === AUTH_STATE?.AUTHENTICATED && userProfile?.role === 'photographer') {
    return null;
  }

  const companyLinks = [
    { path: '/about', label: 'من نحن' },
    { path: '/terms', label: 'الشروط والأحكام' },
    { path: '/privacy', label: 'سياسة الخصوصية' },
  ];

  const serviceLinks = [
    { path: '/browse-photographers', label: 'تصفح المصورين' },
    { path: '/booking-wizard', label: 'احجز الآن' },
    { path: '/photographer-register', label: 'سجل كمصور' },
  ];

  const complianceBadges = [
    {
      name: 'وزارة التجارة',
      url: 'https://mci.gov.sa',
      ariaLabel: 'وزارة التجارة السعودية',
      logo: '/logos/mci.png',
      scale: 1.5,
    },
    {
      name: 'المركز السعودي للأعمال',
      url: 'https://business.gov.sa',
      ariaLabel: 'المركز السعودي للأعمال',
      logo: '/logos/bsc.png',
      scale: 1.5,
    },
    {
      name: 'رؤية 2030',
      url: 'https://vision2030.gov.sa',
      ariaLabel: 'رؤية المملكة العربية السعودية 2030',
      logo: '/logos/vision2030.png',
      scale: 1.3,
    },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="site-footer">
      <div className="site-footer-container">
        {/* Main Footer Content */}
        <div className="site-footer-content">
          {/* Company Info */}
          <div className="site-footer-column">
            <div className="site-footer-logo">
              <MashhadLogo variant="light" size="xl" logoType="footer" />
            </div>
            <p className="site-footer-description">
              منصة رائدة لحجز خدمات التصوير الفوتوغرافي في المملكة العربية السعودية. نربط بين المصورين المحترفين والعملاء بشكل سلس وآمن.
            </p>
            {/* بادجات الجهات الحكومية */}
            <div className="flex flex-wrap gap-2 mt-4">
              {complianceBadges?.map((badge) => (
                <a
                  key={badge?.name}
                  href={badge?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={badge?.ariaLabel}
                  className="flex items-center justify-center rounded-xl overflow-hidden transition-all duration-200 hover:scale-105"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    padding: '4px 8px',
                    height: '40px',
                    minWidth: '72px',
                    maxWidth: '88px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                  }}
                >
                  <img
                    src={badge?.logo}
                    alt={badge?.ariaLabel}
                    className="object-contain"
                    style={{ width: `${badge?.scale * 100}%`, height: `${badge?.scale * 100}%`, transform: `scale(${badge?.scale})` }}
                  />
                </a>
              ))}
            </div>
          </div>

          {/* Company Links */}
          <div className="site-footer-column">
            <h3 className="site-footer-heading">الشركة</h3>
            <ul className="site-footer-links">
              {companyLinks?.map((link) => (
                <li key={link?.path}>
                  <button
                    onClick={() => handleNavigation(link?.path)}
                    className="site-footer-link"
                  >
                    {link?.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Links */}
          <div className="site-footer-column">
            <h3 className="site-footer-heading">الخدمات</h3>
            <ul className="site-footer-links">
              {serviceLinks?.map((link) => (
                <li key={link?.path}>
                  <button
                    onClick={() => handleNavigation(link?.path)}
                    className="site-footer-link"
                  >
                    {link?.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="site-footer-column">
            <h3 className="site-footer-heading">تواصل معنا</h3>
            <div className="site-footer-contact">
              <div className="site-footer-contact-item">
                <Icon name="Phone" size={18} />
                <span dir="ltr">{'+966 57 195 4344'}</span>
              </div>
              <div className="site-footer-contact-item">
                <Icon name="Mail" size={18} />
                <span>info@mashhad.sa</span>
              </div>
              <div className="site-footer-contact-item">
                <Icon name="MapPin" size={18} />
                <span>الدمام، المملكة العربية السعودية</span>
              </div>
            </div>
            {/* Trust Badges */}
            <div className="site-footer-badges">
              <div className="site-footer-badge">
                <Icon name="Shield" size={20} color="var(--color-success)" />
                <span>دفع آمن</span>
              </div>
              <div className="site-footer-badge">
                <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                <span>موثوق</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="site-footer-bottom">
          <div className="site-footer-bottom-content">
            <p className="site-footer-copyright">
              © {formatNumber(2026)} Mashhad Technologies
            </p>
            <div className="site-footer-payment">
              <span className="site-footer-payment-label">طرق الدفع:</span>
              <div className="site-footer-payment-icons">
                <Icon name="CreditCard" size={24} />
                <Icon name="Wallet" size={24} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;